import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { trackRecordService } from '../../services/trackRecordService';
import { TimelineEvent, TrackRecordSummary } from '../../types/trackRecord';
import { fmt } from '../../utils/formatters';
import TradeCard from '../shared/TradeCard';

const PAGE_SIZE = 20;

const TrackRecord: React.FC = () => {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [summary, setSummary] = useState<TrackRecordSummary | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [ticker, setTicker] = useState<string | null>(null);
  const [tickers, setTickers] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [timeline, sum] = await Promise.all([
        trackRecordService.getTimeline({
          ticker: ticker || undefined,
          page,
          page_size: PAGE_SIZE,
        }),
        trackRecordService.getSummary(),
      ]);
      setEvents(timeline.items);
      setTotal(timeline.total);
      setSummary(sum);

      // Extract unique tickers from events
      const unique = Array.from(new Set(timeline.items.map((e) => e.ticker))).sort();
      setTickers((prev) => {
        const merged = Array.from(new Set([...prev, ...unique])).sort();
        return merged;
      });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [page, ticker]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const pnlColor = summary ? (Number(summary.total_pnl) >= 0 ? 'success.main' : 'error.main') : 'text.secondary';

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Earnings Signal Performance
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Every Pryvo recommendation, paper-traded at real market prices.
      </Typography>

      {/* Summary banner */}
      {summary && (
        <Paper
          elevation={2}
          sx={{
            p: { xs: 2, sm: 2.5 },
            mb: 3,
            display: 'flex',
            flexWrap: 'wrap',
            gap: { xs: 2, sm: 4 },
            alignItems: 'center',
            justifyContent: 'center',
            borderLeft: 4,
            borderColor: Number(summary.total_pnl) >= 0 ? 'success.main' : 'error.main',
          }}
        >
          <Box sx={{ textAlign: 'center', minWidth: 100 }}>
            <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
              Total G/L
            </Typography>
            <Typography variant="h5" fontWeight={800} color={pnlColor}>
              {Number(summary.total_pnl) >= 0 ? '+' : ''}{fmt.currency(Number(summary.total_pnl))}
            </Typography>
          </Box>

          <Divider orientation="vertical" flexItem />

          <Box sx={{ textAlign: 'center', minWidth: 80 }}>
            <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
              Avg ROI
            </Typography>
            <Typography variant="h5" fontWeight={800} color={pnlColor}>
              {Number(summary.avg_roi) >= 0 ? '+' : ''}{Number(summary.avg_roi).toFixed(1)}%
            </Typography>
          </Box>

          <Divider orientation="vertical" flexItem />

          <Box sx={{ textAlign: 'center', minWidth: 80 }}>
            <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
              Avg APR
            </Typography>
            <Typography variant="h5" fontWeight={800} color={pnlColor}>
              {Number(summary.avg_apr) >= 0 ? '+' : ''}{Number(summary.avg_apr).toFixed(0)}%
            </Typography>
          </Box>

          <Divider orientation="vertical" flexItem />

          <Box sx={{ textAlign: 'center', minWidth: 80 }}>
            <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
              Win Rate
            </Typography>
            <Typography variant="h5" fontWeight={800}>
              {Number(summary.win_rate).toFixed(0)}%
            </Typography>
          </Box>

          <Divider orientation="vertical" flexItem />

          <Box sx={{ textAlign: 'center', minWidth: 80 }}>
            <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
              Avg Days
            </Typography>
            <Typography variant="h5" fontWeight={800}>
              {Number(summary.avg_days).toFixed(1)}
            </Typography>
          </Box>

          <Divider orientation="vertical" flexItem />

          <Box sx={{ textAlign: 'center', minWidth: 80 }}>
            <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
              Total Plays
            </Typography>
            <Typography variant="h5" fontWeight={800}>
              {summary.total_closed + summary.open_count}
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Ticker filter */}
      {tickers.length > 0 && (
        <Autocomplete
          options={tickers}
          value={ticker}
          onChange={(_, v) => { setTicker(v); setPage(1); }}
          renderInput={(params) => (
            <TextField {...params} label="Filter by ticker" size="small" placeholder="All tickers" />
          )}
          sx={{ maxWidth: 156, mb: 2 }}
          clearOnEscape
        />
      )}

      {/* Loading / Error */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Timeline */}
      {!loading && events.length === 0 && (
        <Alert severity="info">No paper trades yet. Trades will appear here once Pryvo opens its first recommendation.</Alert>
      )}
      {!loading && events.map((event, i) => (
        <TradeCard key={`${event.ticker}-${event.event_type}-${event.event_time}-${i}`} event={event} />
      ))}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 2 }}>
          <IconButton disabled={page <= 1} onClick={() => setPage((p) => p - 1)} size="small">
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="body2">
            Page {page} of {totalPages}
          </Typography>
          <IconButton disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} size="small">
            <ChevronRightIcon />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default TrackRecord;
